import { supabase } from '../../../lib/supabase';

// Page reading runs in the ocr-movement-page edge function so the Anthropic key
// stays on the server and only signed-in users can spend credits. The prompt
// lives with the function.

const FUNCTION_NAME = 'ocr-movement-page';

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read that image'));
    img.src = src;
  });
}

// Phone photos are far larger than the model needs. Downscaling keeps the
// request comfortably inside the function's payload budget and speeds the read
// up; 2000px on the long edge still resolves handwritten item codes.
export async function prepareImage(dataUrl, maxDim = 2000, quality = 0.9) {
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // Paint white under the picture before drawing it, so a see-through PNG is
  // read as dark marks on white. **This must stay a real colour**: a canvas
  // cannot read CSS variables and silently keeps its default, black, which is
  // exactly what 'var(--oh-white)' did from 11 to 14 September 2026, flattening
  // see-through pictures onto black. Not a theme colour; the page reader needs
  // white whatever theme the person has chosen.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const out = canvas.toDataURL('image/jpeg', quality);
  return { imageBase64: out.split(',')[1], mediaType: 'image/jpeg' };
}

// On any non-2xx, supabase-js hands back the raw Response instead of parsing it,
// so the function's own explanation - the only part worth reading - sits unread
// inside error.context. Without this you get "Edge Function returned a non-2xx
// status code" and no way to tell a missing key from a bad image.
async function messageFrom(error, data) {
  if (data && data.error) return data.error;

  const res = error?.context;
  if (res && typeof res.text === 'function') {
    let raw = '';
    try { raw = await res.clone().text(); }
    catch { try { raw = await res.text(); } catch { /* body already spent */ } }
    if (raw) {
      try {
        const body = JSON.parse(raw);
        if (body?.error) return body.error;
      } catch { return raw.slice(0, 300); }
    }
  }

  if (res?.status === 503) return 'Page reading is not configured on the server yet.';
  if (res?.status) return 'Page reading failed (HTTP ' + res.status + ').';
  return error?.message || 'Page reading failed';
}

// Turns the server's description of the stored key into something an admin can
// act on. A key that is rejected has two very different causes - a bad paste,
// or a key that is genuinely dead - and they are not worth confusing.
function describeKey(shape) {
  if (!shape) return '';
  if (shape.hadSurroundingWhitespace) return 'The stored key has spaces or a line break around it, which Anthropic rejects. Set it again without the stray whitespace.';
  if (shape.hasInnerWhitespace) return 'The stored key has whitespace inside it, so it was probably pasted broken across lines.';
  if (!shape.looksLikeAnthropicKey) return 'The stored key does not start with "sk-ant-", so it may not be an Anthropic key.';
  if (shape.length < 40) return 'The stored key is only ' + shape.length + ' characters, which suggests the paste was cut short.';
  return '';
}

// Sends one page image and returns the parsed {page_date, sections} object.
export async function ocrImage(img) {
  const payload = await prepareImage(img.dataUrl);
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body: payload });
  if (error || !data?.page) throw new Error(await messageFrom(error, data));
  return data.page;
}

// Setup tab readiness check: is the server holding a key?
export async function checkOcrService() {
  try {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body: { ping: true } });
    if (error) return { ok: false, reason: await messageFrom(error, data) };
    if (!data?.configured) return { ok: false, reason: 'The server has no Anthropic key set.' };
    return { ok: true, reason: '', keyNote: describeKey(data.keyShape) };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
}
